import {
  buildTreeFromPaths,
  countPageTreeNodes,
  databasesByAncestorPage,
  pageSubtreeIds,
  parseNotionIndexHtml,
  type EnrichedExportNode,
  type ExportCatalog,
  type ExportDatabaseEntry,
  type NotionNodeKind,
} from '@couplespace/core/notion-export';
import {
  createElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactElement,
} from 'react';
import { ActivityIndicator, View } from 'react-native';
import { useRouter } from 'expo-router';

import { DocumentTitle } from '@/components/layout/DocumentTitle';
import { useSidebar } from '@/components/layout/SidebarContext';
import { Button, Text } from '@/components/ui';
import { createBlock, fetchCurrentMember } from '@/lib/blocks/api';
import { fetchBlockTree as fetchRemoteBlockTree } from '@/lib/blocks/api.web';
import { findFamilyRoot, findPersonalRoot } from '@/lib/blocks/constants';
import { isOnline } from '@/lib/connectivity';
import { buildImportCatalog } from '@/lib/notion-export/classify-export';
import {
  buildImportPlan,
  buildImportKindMap,
  buildPlannedIdMap,
  childNotionIdsByParent,
  planItemToBatchPayload,
  postNotionImportBatch,
} from '@/lib/notion-export/build-import-plan';
import { wipeWorkspaceForImport } from '@/lib/notion-export/api.web';
import {
  scanDirectoryHandle,
  scanFileList,
  type ExportFolderScan,
} from '@/lib/notion-export/read-export-folder';
import { getSyncEngine } from '@/lib/sync/engine';

type Step = 'pick' | 'select' | 'run' | 'done';
type MountTarget = 'family' | 'personal';

type ImportSummary = {
  pages: number;
  databases: number;
  rows: number;
  uploadFailures: string[];
  itemFailures: Array<{ notionId: string; kind: string; message: string }>;
};

function computeAutoDatabaseIds(
  catalog: ExportCatalog,
  selectedPageIds: Set<string>,
): Set<string> {
  const byAncestor = databasesByAncestorPage(catalog);
  const auto = new Set<string>();
  for (const pageId of selectedPageIds) {
    const subtree = pageSubtreeIds(catalog.enrichedRoots, pageId);
    if (!subtree) {
      continue;
    }
    for (const pid of subtree) {
      const dbs = byAncestor.get(pid);
      if (dbs) {
        for (const dbId of dbs) {
          auto.add(dbId);
        }
      }
    }
  }
  return auto;
}

function kindBadge(kind: NotionNodeKind): string | null {
  if (kind === 'database_row_pages') {
    return 'row page';
  }
  return null;
}

function PageTreeCheckbox({
  node,
  depth,
  selected,
  onToggle,
}: {
  node: EnrichedExportNode;
  depth: number;
  selected: Set<string>;
  onToggle: (id: string, checked: boolean) => void;
}): ReactElement {
  const id = node.notionId;
  const checked = id != null && selected.has(id);
  const count = countPageTreeNodes(node);
  const badge = kindBadge(node.kind);

  return createElement(
    'div',
    { className: 'block w-full', style: { paddingLeft: depth * 16 } },
    id && node.href
      ? createElement(
          'label',
          {
            className:
              'flex w-full cursor-pointer items-start gap-2 py-1.5 text-sm text-fg',
          },
          createElement('input', {
            type: 'checkbox',
            className: 'mt-0.5 shrink-0',
            checked,
            onChange: (e: Event) => onToggle(id, (e.target as HTMLInputElement).checked),
          }),
          createElement(
            'span',
            { className: 'min-w-0 flex-1 leading-snug' },
            node.displayTitle,
            badge
              ? createElement(
                  'span',
                  { className: 'ml-2 rounded bg-warning-muted px-1.5 py-0.5 text-xs text-fg' },
                  badge,
                )
              : null,
            createElement(
              'span',
              { className: 'text-muted' },
              ` (${count} page${count === 1 ? '' : 's'})`,
            ),
          ),
        )
      : createElement(
          'div',
          { className: 'py-1.5 text-sm text-muted' },
          node.displayTitle || 'Untitled',
        ),
    ...node.children.map((child) =>
      createElement(PageTreeCheckbox, {
        key: child.notionId ?? child.href,
        node: child,
        depth: depth + 1,
        selected,
        onToggle,
      }),
    ),
  );
}

function DatabaseEntryRow({
  db,
  selectedDatabaseIds,
  onToggleDatabase,
}: {
  db: ExportDatabaseEntry;
  selectedDatabaseIds: Set<string>;
  onToggleDatabase: (id: string, checked: boolean) => void;
}): ReactElement {
  const checked = selectedDatabaseIds.has(db.notionId);
  const rowCount = db.rowPages.length;

  return createElement(
    'div',
    { className: 'border-b border-border py-2 last:border-b-0' },
    createElement(
      'label',
      { className: 'flex cursor-pointer items-start gap-2 text-sm font-medium text-fg' },
      createElement('input', {
        type: 'checkbox',
        className: 'mt-0.5 shrink-0',
        checked,
        onChange: (e: Event) =>
          onToggleDatabase(db.notionId, (e.target as HTMLInputElement).checked),
      }),
      createElement(
        'span',
        { className: 'min-w-0 flex-1' },
        db.displayTitle,
        db.isFullExport
          ? createElement(
              'span',
              { className: 'ml-2 rounded bg-violet-100 px-1.5 py-0.5 text-xs text-violet-900' },
              'Database',
            )
          : createElement(
              'span',
              { className: 'ml-2 rounded bg-slate-100 px-1.5 py-0.5 text-xs text-slate-600' },
              'linked view',
            ),
        createElement(
          'span',
          { className: 'ml-2 text-xs text-muted' },
          rowCount > 0 ? `${rowCount} rows` : 'CSV rows',
        ),
        db.location
          ? createElement(
              'span',
              { className: 'ml-2 text-xs text-muted/70' },
              db.location,
            )
          : null,
      ),
    ),
  );
}

export function NotionImportWizard() {
  const router = useRouter();
  const { refreshTree, expandToPage } = useSidebar();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [step, setStep] = useState<Step>('pick');
  const [error, setError] = useState<string | null>(null);
  const [scan, setScan] = useState<ExportFolderScan | null>(null);
  const [catalog, setCatalog] = useState<ExportCatalog | null>(null);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [workspaceName, setWorkspaceName] = useState<string | null>(null);
  const [selectedPageIds, setSelectedPageIds] = useState<Set<string>>(new Set());
  const [selectedDatabaseIds, setSelectedDatabaseIds] = useState<Set<string>>(new Set());
  const [mountTarget, setMountTarget] = useState<MountTarget>('family');
  const [useWrapper, setUseWrapper] = useState(false);
  const [wipeFirst, setWipeFirst] = useState(false);
  const [memberName, setMemberName] = useState('');
  const [progress, setProgress] = useState({ done: 0, total: 0, label: '' });
  const [importedRootId, setImportedRootId] = useState<string | null>(null);
  const [importSummary, setImportSummary] = useState<ImportSummary | null>(null);
  const [running, setRunning] = useState(false);

  const showWipeOption = !import.meta.env.PROD;

  useEffect(() => {
    void fetchCurrentMember()
      .then((m) => setMemberName(m.displayName))
      .catch(() => {});
  }, []);

  const autoDatabaseIds = useMemo(() => {
    if (!catalog) {
      return new Set<string>();
    }
    return computeAutoDatabaseIds(catalog, selectedPageIds);
  }, [catalog, selectedPageIds]);

  const effectiveDatabaseIds = useMemo(() => {
    const merged = new Set(selectedDatabaseIds);
    for (const id of autoDatabaseIds) {
      merged.add(id);
    }
    return merged;
  }, [selectedDatabaseIds, autoDatabaseIds]);

  const stats = useMemo(() => {
    if (!catalog) {
      return { pages: 0, databases: 0, autoDatabases: 0 };
    }
    const plan = buildImportPlan(catalog, selectedPageIds, effectiveDatabaseIds);
    const databases = plan.filter((p) => p.kind === 'database').length;
    return {
      pages: plan.length - databases,
      databases,
      autoDatabases: autoDatabaseIds.size,
    };
  }, [catalog, selectedPageIds, effectiveDatabaseIds, autoDatabaseIds.size]);

  const selectionCount = stats.pages + stats.databases;

  const loadScan = useCallback(async (next: ExportFolderScan) => {
    setCatalogLoading(true);
    setError(null);
    try {
      // Older exports ship index.html (authoritative titles + order). Newer
      // ones don't, so reconstruct the tree from the folder layout instead.
      const parsed = next.indexHtml ? parseNotionIndexHtml(next.indexHtml) : null;
      const roots = parsed && parsed.roots.length > 0 ? parsed.roots : buildTreeFromPaths(next.paths);
      setScan(next);
      setWorkspaceName(parsed?.workspaceName ?? next.rootName ?? null);
      setSelectedPageIds(new Set());
      setSelectedDatabaseIds(new Set());
      const built = await buildImportCatalog(next, roots);
      setCatalog(built);
      setStep('select');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to read export');
      setCatalog(null);
    } finally {
      setCatalogLoading(false);
    }
  }, []);

  async function onPickDirectory(): Promise<void> {
    setError(null);
    try {
      if ('showDirectoryPicker' in window) {
        const dir = await (
          window as Window & {
            showDirectoryPicker: () => Promise<FileSystemDirectoryHandle>;
          }
        ).showDirectoryPicker();
        await loadScan(await scanDirectoryHandle(dir));
        return;
      }
      fileInputRef.current?.click();
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') {
        return;
      }
      setError(err instanceof Error ? err.message : 'Failed to open folder');
    }
  }

  async function onFolderInputChange(list: FileList | null): Promise<void> {
    if (!list?.length) {
      return;
    }
    try {
      await loadScan(await scanFileList(list));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to read folder');
    }
  }

  function onTogglePage(id: string, checked: boolean): void {
    setSelectedPageIds((prev) => {
      const next = new Set(prev);
      if (checked) {
        next.add(id);
      } else {
        next.delete(id);
      }
      return next;
    });
  }

  function onToggleDatabase(id: string, checked: boolean): void {
    setSelectedDatabaseIds((prev) => {
      const next = new Set(prev);
      if (checked) {
        next.add(id);
      } else {
        next.delete(id);
      }
      return next;
    });
  }

  async function resolveMountParentId(): Promise<string> {
    const member = await fetchCurrentMember();
    setMemberName(member.displayName);
    // The sidebar tree is local SQLite and can still hold a Family id from
    // before a server wipe. Import writes on the server, so the destination
    // has to be the id the API currently returns.
    const topLevel = await fetchRemoteBlockTree(null);
    const pageRoots = topLevel.filter((b) => b.type === 'page');
    const family = findFamilyRoot(pageRoots);
    const personal = findPersonalRoot(pageRoots, member.id);
    const base = mountTarget === 'family' ? family : personal;
    if (!base) {
      throw new Error(
        'Could not find the import destination on the server. Use Settings → Full resync, then try again.',
      );
    }
    if (!useWrapper) {
      return base.id;
    }
    const wrapper = await createBlock({
      parentId: base.id,
      type: 'page',
      title: `Notion import (${new Date().toISOString().slice(0, 10)})`,
      content: {},
    });
    return wrapper.id;
  }

  async function onRunImport(): Promise<void> {
    if (!scan || !catalog || selectionCount === 0) {
      setError('Select at least one page or database to import.');
      return;
    }
    if (!isOnline()) {
      setError('Import requires an online connection.');
      return;
    }

    setRunning(true);
    setError(null);
    setStep('run');

    try {
      if (wipeFirst) {
        setProgress({ done: 0, total: 1, label: 'Wiping workspace…' });
        await wipeWorkspaceForImport();
        await getSyncEngine().resyncAfterWorkspaceWipe();
      }

      const mountParentId = await resolveMountParentId();
      const plan = buildImportPlan(catalog, selectedPageIds, effectiveDatabaseIds);
      const kindMap = buildImportKindMap(plan);
      const childrenByParent = childNotionIdsByParent(plan);
      let idMap = buildPlannedIdMap(plan);
      console.log('[CS-NOTION] run:start', {
        mountTarget,
        mountParentId,
        selectedPageIds: [...selectedPageIds],
        effectiveDatabaseIds: [...effectiveDatabaseIds],
        planItems: plan.map((p) => ({
          kind: p.kind,
          notionId: p.notionId,
          parentNotionId: p.parentNotionId,
          title: p.title,
          path: p.kind === 'page' ? p.mdPath : p.csvPath,
        })),
      });
      const BATCH = 20;
      const summary: ImportSummary = {
        pages: 0,
        databases: 0,
        rows: 0,
        uploadFailures: [],
        itemFailures: [],
      };

      for (let i = 0; i < plan.length; i += BATCH) {
        const slice = plan.slice(i, i + BATCH);
        const items = await Promise.all(
          slice.map((planItem) =>
            planItemToBatchPayload(planItem, scan, idMap, kindMap, mountParentId, childrenByParent),
          ),
        );
        for (const item of items) {
          if (item.kind === 'page' && item.uploadFailures?.length) {
            summary.uploadFailures.push(...item.uploadFailures);
          }
          if (item.kind === 'database' && item.uploadFailures?.length) {
            summary.uploadFailures.push(...item.uploadFailures);
          }
        }
        setProgress({
          done: i,
          total: plan.length,
          label: slice[0]?.title ?? '',
        });
        console.log('[CS-NOTION] batch:send', {
          mountParentId,
          idMapKeys: Object.keys(idMap).length,
          items: items.map((it) => ({
            kind: it.kind,
            notionId: it.notionId,
            parentNotionId: it.parentNotionId,
            title: it.title,
            contentBlocks: it.kind === 'page' ? (it.contentBlocks?.length ?? 0) : undefined,
          })),
        });
        const result = await postNotionImportBatch({ mountParentId, idMap, items });
        console.log('[CS-NOTION] batch:result', {
          pagesCreated: result.pagesCreated,
          databasesCreated: result.databasesCreated,
          rowsInserted: result.rowsInserted,
          idMap: result.idMap,
          failures: result.failures,
        });
        idMap = { ...idMap, ...result.idMap };
        summary.pages += result.pagesCreated;
        summary.databases += result.databasesCreated;
        summary.rows += result.rowsInserted;
        summary.itemFailures.push(...result.failures);
      }

      // Open the first top-level imported page (a selection root: no parent, or
      // whose parent was not itself imported) rather than the mount container,
      // which is the workspace root and renders an empty state.
      const planNotionIds = new Set(plan.map((p) => p.notionId));
      const rootPlanItem =
        plan.find((p) => !p.parentNotionId || !planNotionIds.has(p.parentNotionId)) ?? plan[0];
      const openId = (rootPlanItem && idMap[rootPlanItem.notionId]) || mountParentId;
      console.log('[CS-NOTION] run:done', {
        mountParentId,
        openId,
        rootNotionId: rootPlanItem?.notionId,
        idMap,
      });

      await getSyncEngine().catchup(true);
      refreshTree();
      await expandToPage(openId);
      setImportedRootId(openId);
      setImportSummary(summary);
      setStep('done');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Import failed');
      setStep('select');
    } finally {
      setRunning(false);
    }
  }

  return (
    <View className="flex-1 bg-surface px-page py-page-y">
      <DocumentTitle title="Import from Notion" />
      <Text variant="h1">Import from Notion</Text>
      <Text variant="caption" className="mt-2">
        Choose an unzipped Notion Markdown &amp; CSV export folder (with or without index.html).
      </Text>

      {error ? (
        <Text variant="caption" className="mt-4 text-danger">
          {error}
        </Text>
      ) : null}

      {catalogLoading ? (
        <View className="mt-8 items-start">
          <ActivityIndicator />
          <Text variant="caption" className="mt-2">
            Scanning export and classifying pages vs databases…
          </Text>
        </View>
      ) : null}

      {step === 'pick' && !catalogLoading ? (
        <View className="mt-8">
          <Button variant="primary" label="Choose export folder" onPress={() => void onPickDirectory()} />
          <input
            ref={fileInputRef}
            type="file"
            className="hidden"
            // @ts-expect-error webkitdirectory is non-standard
            webkitdirectory=""
            multiple
            onChange={(e) => void onFolderInputChange(e.target.files)}
          />
        </View>
      ) : null}

      {step === 'select' && scan && catalog ? (
        <View className="mt-6">
          {workspaceName ? (
            <Text variant="caption">Workspace: {workspaceName}</Text>
          ) : null}

          <Text variant="body" className="mt-4 font-medium">
            Pages
          </Text>
          <Text variant="caption" className="mt-1">
            Regular wiki pages and standalone content (not Notion databases).
          </Text>
          {createElement(
            'div',
            {
              className:
                'mt-2 flex max-h-64 flex-col overflow-y-auto rounded-lg border border-border p-3',
            },
            catalog.pageTree.length === 0
              ? createElement('p', { className: 'text-sm text-muted' }, 'No standalone pages in export.')
              : catalog.pageTree.map((root) =>
                  createElement(PageTreeCheckbox, {
                    key: root.notionId ?? root.href,
                    node: root,
                    depth: 0,
                    selected: selectedPageIds,
                    onToggle: onTogglePage,
                  }),
                ),
          )}

          <Text variant="body" className="mt-6 font-medium">
            Databases
          </Text>
          <Text variant="caption" className="mt-1">
            {catalog.databases.length === 0
              ? 'No Notion databases found in this export.'
              : `${catalog.databases.length} database${catalog.databases.length === 1 ? '' : 's'} found. Selecting a database imports it as a table with all its rows from the CSV.`}
          </Text>
          {createElement(
            'div',
            {
              className:
                'mt-2 flex max-h-64 flex-col overflow-y-auto rounded-lg border border-border px-3',
            },
            catalog.databases.length === 0
              ? createElement('p', { className: 'py-3 text-sm text-muted' }, 'No databases in this export.')
              : catalog.databases.map((db) =>
                  createElement(DatabaseEntryRow, {
                    key: db.notionId,
                    db,
                    selectedDatabaseIds,
                    onToggleDatabase,
                  }),
                ),
          )}

          <Text variant="caption" className="mt-2">
            Selected: {stats.pages} page{stats.pages === 1 ? '' : 's'}
            {stats.databases > 0
              ? `, ${stats.databases} database${stats.databases === 1 ? '' : 's'}`
              : ''}
            {stats.autoDatabases > 0
              ? ` (${stats.autoDatabases} auto-included from selected pages)`
              : ''}
          </Text>

          <Text variant="body" className="mt-6 font-medium">
            Import into
          </Text>
          {createElement(
            'div',
            { className: 'mt-2 flex flex-row flex-wrap gap-4' },
            createElement(
              'label',
              { className: 'flex cursor-pointer items-center gap-2 text-sm' },
              createElement('input', {
                type: 'radio',
                name: 'notion-import-mount',
                checked: mountTarget === 'family',
                onChange: () => setMountTarget('family'),
              }),
              'Family',
            ),
            createElement(
              'label',
              { className: 'flex cursor-pointer items-center gap-2 text-sm' },
              createElement('input', {
                type: 'radio',
                name: 'notion-import-mount',
                checked: mountTarget === 'personal',
                onChange: () => setMountTarget('personal'),
              }),
              `Personal${memberName ? ` — ${memberName}` : ''}`,
            ),
          )}

          {createElement(
            'label',
            { className: 'mt-4 flex cursor-pointer items-center gap-2 text-sm' },
            createElement('input', {
              type: 'checkbox',
              checked: useWrapper,
              onChange: (e: Event) => setUseWrapper((e.target as HTMLInputElement).checked),
            }),
            'Create dated wrapper page for this import',
          )}

          {showWipeOption
            ? createElement(
                'label',
                { className: 'mt-4 flex cursor-pointer items-center gap-2 text-sm text-danger' },
                createElement('input', {
                  type: 'checkbox',
                  checked: wipeFirst,
                  onChange: (e: Event) => setWipeFirst((e.target as HTMLInputElement).checked),
                }),
                'Empty entire workspace before import (dev only)',
              )
            : null}

          <View className="mt-8 flex flex-row gap-3">
            <Button
              variant="secondary"
              label="Back"
              onPress={() => {
                setStep('pick');
                setScan(null);
                setCatalog(null);
              }}
            />
            <Button
              variant="primary"
              label="Import"
              disabled={running || selectionCount === 0}
              onPress={() => void onRunImport()}
            />
          </View>
        </View>
      ) : null}

      {step === 'run' ? (
        <View className="mt-8">
          <ActivityIndicator />
          <Text variant="caption" className="mt-4">
            {progress.done} / {progress.total}
            {progress.label ? ` — ${progress.label}` : ''}
          </Text>
        </View>
      ) : null}

      {step === 'done' ? (
        <View className="mt-8 gap-3">
          <Text variant="body">Import finished.</Text>
          {importSummary ? (
            <>
              <Text variant="caption">
                {importSummary.pages} page{importSummary.pages === 1 ? '' : 's'},{' '}
                {importSummary.databases} database{importSummary.databases === 1 ? '' : 's'},{' '}
                {importSummary.rows} row{importSummary.rows === 1 ? '' : 's'} imported.
              </Text>
              {importSummary.uploadFailures.length > 0 ? (
                <>
                  <Text variant="caption" className="text-fg-muted">
                    {importSummary.uploadFailures.length} file upload
                    {importSummary.uploadFailures.length === 1 ? '' : 's'} failed (links kept).
                  </Text>
                  <View className="max-h-40 overflow-auto rounded bg-warning-muted px-3 py-2">
                    {importSummary.uploadFailures.slice(0, 25).map((failure, i) => (
                      <Text key={i} variant="caption" className="text-fg-muted">
                        {failure}
                      </Text>
                    ))}
                    {importSummary.uploadFailures.length > 25 ? (
                      <Text variant="caption" className="text-fg-muted">
                        …and {importSummary.uploadFailures.length - 25} more.
                      </Text>
                    ) : null}
                  </View>
                </>
              ) : null}
              {importSummary.itemFailures.length > 0 ? (
                <>
                  <Text variant="caption" className="text-danger">
                    {importSummary.itemFailures.length} item
                    {importSummary.itemFailures.length === 1 ? '' : 's'} failed to import.
                  </Text>
                  <View className="max-h-40 overflow-auto rounded bg-danger-muted px-3 py-2">
                    {importSummary.itemFailures.slice(0, 25).map((failure, i) => (
                      <Text key={i} variant="caption" className="text-danger">
                        {failure.kind} {failure.notionId}: {failure.message}
                      </Text>
                    ))}
                  </View>
                </>
              ) : null}
            </>
          ) : null}
          <View className="flex flex-row gap-3">
            {importedRootId ? (
              <Button
                variant="primary"
                label="Open imported section"
                onPress={() => router.push(`/page/${importedRootId}`)}
              />
            ) : null}
            <Button variant="secondary" label="Back to Settings" onPress={() => router.push('/settings')} />
          </View>
        </View>
      ) : null}
    </View>
  );
}
